import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { VerificationBadge, RoleBadge } from '../components/Badges';
import FraudShieldAdmin from '../components/FraudShieldAdmin';
import {
  Users, FileText, MessageSquare, Shield, CheckCircle,
  XCircle, Clock, Loader2, RefreshCw, Eye, AlertTriangle, Trash2, ShieldAlert,
  Download, Search, Crown, DollarSign, Building, Phone, Mail,
  BarChart3, TrendingUp, Monitor, Smartphone, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [members, setMembers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberRoleFilter, setMemberRoleFilter] = useState('all');
  const [foundingFilter, setFoundingFilter] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, docsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers({ verification_status: 'pending' }),
        adminAPI.getDocuments('pending'),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    setMembersLoading(true);
    try {
      const params = new URLSearchParams();
      if (memberRoleFilter !== 'all') params.append('role', memberRoleFilter);
      if (foundingFilter) params.append('founding_only', 'true');
      if (memberSearch) params.append('search', memberSearch);
      
      const res = await api.get(`/admin/members?${params.toString()}`);
      setMembers(res.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get(`/admin/analytics?days=${analyticsPeriod}`);
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Load analytics when tab changes to analytics
  useEffect(() => {
    if (activeTab === 'analytics' && !analytics) {
      loadAnalytics();
    }
  }, [activeTab]);

  // Reload analytics when period changes
  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [analyticsPeriod]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get('/admin/members/export', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `haullogix_members_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Members exported to CSV');
    } catch (error) {
      toast.error('Failed to export members');
    } finally {
      setExporting(false);
    }
  };

  // Load members when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'members') {
      loadMembers();
    }
  }, [activeTab, memberRoleFilter, foundingFilter]);

  const handleCleanupTestData = async () => {
    setCleaning(true);
    try {
      const response = await api.delete('/admin/cleanup-test-data');
      toast.success(`Cleaned: ${response.data.loads_deleted} loads, ${response.data.test_users_deleted} test users`);
      setCleanupDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to cleanup test data');
    } finally {
      setCleaning(false);
    }
  };

  const handleVerifyUser = async (userId, status) => {
    try {
      await adminAPI.verifyUser(userId, { verification_status: status });
      toast.success(`User ${status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleReviewDocument = async (docId, status, notes = '') => {
    try {
      await adminAPI.reviewDocument(docId, { status, notes });
      toast.success(`Document ${status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to review document');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-3 sm:px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8 animate-fade-in">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold uppercase font-['Barlow_Condensed'] tracking-tight flex items-center gap-2 sm:gap-3">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              Manage users, documents, and platform settings
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 flex-1 sm:flex-none text-xs sm:text-sm"
              onClick={() => setCleanupDialogOpen(true)}
              data-testid="cleanup-btn"
            >
              <Trash2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Clear Test Data</span>
              <span className="sm:hidden">Clear</span>
            </Button>
            <Button variant="outline" onClick={loadData} data-testid="refresh-btn" className="flex-1 sm:flex-none">
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Stats Overview - Scrollable on mobile */}
        {stats && (
          <div className="overflow-x-auto pb-2 mb-6 sm:mb-8 -mx-3 px-3 sm:mx-0 sm:px-0 animate-fade-in">
            <div className="flex sm:grid sm:grid-cols-4 gap-3 sm:gap-4 min-w-max sm:min-w-0">
              <Card className="border-white/5 bg-card/50 min-w-[130px] sm:min-w-0" data-testid="stat-users">
                <CardContent className="p-4 sm:pt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-muted-foreground text-xs sm:text-sm">Total Users</p>
                      <p className="text-2xl sm:text-3xl font-bold font-['Barlow_Condensed']">{stats.total_users}</p>
                    </div>
                    <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-white/5 bg-card/50 min-w-[130px] sm:min-w-0" data-testid="stat-pending">
                <CardContent className="p-4 sm:pt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap">Pending</p>
                      <p className="text-2xl sm:text-3xl font-bold font-['Barlow_Condensed']">{stats.pending_verification}</p>
                    </div>
                    <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-white/5 bg-card/50 min-w-[130px] sm:min-w-0" data-testid="stat-loads">
                <CardContent className="p-4 sm:pt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-muted-foreground text-xs sm:text-sm">Active Loads</p>
                      <p className="text-2xl sm:text-3xl font-bold font-['Barlow_Condensed']">{stats.active_loads}</p>
                    </div>
                    <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-white/5 bg-card/50 min-w-[130px] sm:min-w-0" data-testid="stat-flagged">
                <CardContent className="p-4 sm:pt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-muted-foreground text-xs sm:text-sm">Flagged</p>
                      <p className="text-2xl sm:text-3xl font-bold font-['Barlow_Condensed']">{stats.flagged_posts}</p>
                    </div>
                    <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Tabs - Scrollable on mobile */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
            <TabsList className="mb-4 sm:mb-6 min-w-max sm:min-w-0">
              <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-xs sm:text-sm">
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="members" data-testid="tab-members" className="text-xs sm:text-sm">
                <Crown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Members
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users" className="text-xs sm:text-sm">
                Users ({users.length})
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents" className="text-xs sm:text-sm">
                Docs ({documents.length})
              </TabsTrigger>
              <TabsTrigger value="fraud-shield" data-testid="tab-fraud-shield" className="text-xs sm:text-sm">
                <ShieldAlert className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Fraud
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Pending Users Preview */}
              <Card className="border-white/5 bg-card/50">
                <CardHeader>
                  <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide">
                    Pending Verifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {users.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No pending verifications
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {users.slice(0, 5).map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-3 rounded-sm bg-secondary/30"
                        >
                          <div>
                            <p className="font-medium">{user.full_name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <RoleBadge role={user.role} />
                              <span className="text-xs text-muted-foreground">{user.email}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleVerifyUser(user.user_id, 'verified')}
                              className="h-8"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleVerifyUser(user.user_id, 'rejected')}
                              className="h-8"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pending Documents Preview */}
              <Card className="border-white/5 bg-card/50">
                <CardHeader>
                  <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide">
                    Pending Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {documents.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No pending documents
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {documents.slice(0, 5).map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 rounded-sm bg-secondary/30"
                        >
                          <div>
                            <p className="font-medium capitalize">
                              {doc.document_type.replace('_', ' ')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              User: {doc.user_id.slice(0, 8)}...
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleReviewDocument(doc.id, 'approved')}
                              className="h-8"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReviewDocument(doc.id, 'rejected', 'Document rejected')}
                              className="h-8"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Period Selector */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#4ADE80]" />
                  Site Analytics
                </h2>
                <div className="flex items-center gap-2">
                  <Select value={analyticsPeriod.toString()} onValueChange={(v) => setAnalyticsPeriod(parseInt(v))}>
                    <SelectTrigger className="w-32 bg-[#0A1628]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => loadAnalytics()}
                    disabled={analyticsLoading}
                  >
                    {analyticsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {analyticsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
                </div>
              ) : analytics ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/10 flex items-center justify-center">
                            <Eye className="w-5 h-5 text-[#4ADE80]" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-white">{analytics.total_page_views?.toLocaleString()}</p>
                            <p className="text-xs text-gray-400">Total Page Views</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center">
                            <Users className="w-5 h-5 text-[#3B82F6]" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-white">{analytics.unique_visitors?.toLocaleString()}</p>
                            <p className="text-xs text-gray-400">Unique Visitors</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-yellow-400" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-white">{analytics.page_views_today?.toLocaleString()}</p>
                            <p className="text-xs text-gray-400">Views Today</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <Users className="w-5 h-5 text-purple-400" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-white">{analytics.visitors_today?.toLocaleString()}</p>
                            <p className="text-xs text-gray-400">Visitors Today</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Popular Pages */}
                    <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Globe className="w-4 h-4 text-[#4ADE80]" />
                          Popular Pages
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {analytics.popular_pages?.map((page, idx) => (
                            <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white truncate">{page.page_path || page._id}</p>
                                <p className="text-xs text-gray-500">{page.unique_visitors} unique visitors</p>
                              </div>
                              <span className="text-sm font-medium text-[#4ADE80] ml-2">{page.views}</span>
                            </div>
                          ))}
                          {(!analytics.popular_pages || analytics.popular_pages.length === 0) && (
                            <p className="text-gray-500 text-sm py-4 text-center">No data yet</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Device Breakdown */}
                    <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Monitor className="w-4 h-4 text-[#3B82F6]" />
                          Devices
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-3">
                          {Object.entries(analytics.device_breakdown || {}).map(([device, count]) => {
                            const total = Object.values(analytics.device_breakdown).reduce((a, b) => a + b, 0);
                            const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                            return (
                              <div key={device} className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="flex items-center gap-2 capitalize">
                                    {device === 'mobile' && <Smartphone className="w-4 h-4 text-purple-400" />}
                                    {device === 'desktop' && <Monitor className="w-4 h-4 text-blue-400" />}
                                    {device === 'tablet' && <Monitor className="w-4 h-4 text-green-400" />}
                                    {device}
                                  </span>
                                  <span className="text-gray-400">{count} ({percent}%)</span>
                                </div>
                                <div className="h-2 bg-[#0A1628] rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${
                                      device === 'mobile' ? 'bg-purple-500' : 
                                      device === 'desktop' ? 'bg-blue-500' : 'bg-green-500'
                                    }`}
                                    style={{ width: `${percent}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                          {Object.keys(analytics.device_breakdown || {}).length === 0 && (
                            <p className="text-gray-500 text-sm py-4 text-center">No data yet</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Traffic Chart - Simple bar representation */}
                  <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-yellow-400" />
                        Traffic (Last {analyticsPeriod} Days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-end gap-1 h-32 overflow-x-auto">
                        {analytics.traffic_by_day?.map((day, idx) => {
                          const maxViews = Math.max(...analytics.traffic_by_day.map(d => d.views), 1);
                          const height = (day.views / maxViews) * 100;
                          return (
                            <div key={idx} className="flex-1 min-w-[20px] flex flex-col items-center gap-1">
                              <div className="w-full flex flex-col items-center">
                                <span className="text-[10px] text-gray-500">{day.views}</span>
                                <div 
                                  className="w-full bg-gradient-to-t from-[#4ADE80] to-[#22C55E] rounded-t"
                                  style={{ height: `${Math.max(height, 4)}px` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {(!analytics.traffic_by_day || analytics.traffic_by_day.length === 0) && (
                        <p className="text-gray-500 text-sm py-8 text-center">No traffic data yet. Views will appear as visitors browse the site.</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Recent Visitors */}
                  <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-400" />
                        Recent Visitors
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left py-2 text-gray-400 font-medium">Visitor</th>
                              <th className="text-left py-2 text-gray-400 font-medium">Last Page</th>
                              <th className="text-left py-2 text-gray-400 font-medium">Device</th>
                              <th className="text-left py-2 text-gray-400 font-medium">Pages</th>
                              <th className="text-right py-2 text-gray-400 font-medium">Last Visit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.recent_visitors?.slice(0, 10).map((visitor, idx) => (
                              <tr key={idx} className="border-b border-white/5">
                                <td className="py-2 text-gray-300">{visitor.visitor_id}</td>
                                <td className="py-2 text-white truncate max-w-[150px]">{visitor.last_page}</td>
                                <td className="py-2 capitalize text-gray-400">{visitor.device || '-'}</td>
                                <td className="py-2 text-[#4ADE80]">{visitor.pages_viewed}</td>
                                <td className="py-2 text-right text-gray-500 text-xs">
                                  {visitor.last_visit ? new Date(visitor.last_visit).toLocaleString() : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(!analytics.recent_visitors || analytics.recent_visitors.length === 0) && (
                          <p className="text-gray-500 text-sm py-8 text-center">No visitors yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                  <CardContent className="p-8 text-center">
                    <BarChart3 className="w-12 h-12 mx-auto text-gray-500 mb-3" />
                    <p className="text-gray-400">Click refresh to load analytics</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Members Tab - All Users with Founding Member Info */}
          <TabsContent value="members">
            <Card className="border-white/5 bg-card/50">
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                  <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2 text-base sm:text-lg">
                    <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                    Members
                  </CardTitle>
                  <Button
                    onClick={handleExportCSV}
                    disabled={exporting}
                    className="bg-[#4ADE80] hover:bg-[#22C55E] text-black w-full sm:w-auto"
                    data-testid="export-csv-btn"
                  >
                    {exporting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Export CSV
                  </Button>
                </div>
                
                {/* Filters - Stack on mobile */}
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 mt-4">
                  <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadMembers()}
                      className="pl-10 bg-[#0A1628] border-[#3B82F6]/20"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Select value={memberRoleFilter} onValueChange={setMemberRoleFilter}>
                      <SelectTrigger className="w-full sm:w-[120px] bg-[#0A1628] border-[#3B82F6]/20">
                        <SelectValue placeholder="Roles" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#112240] border-[#3B82F6]/20">
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="carrier">Carrier</SelectItem>
                        <SelectItem value="broker">Broker</SelectItem>
                        <SelectItem value="shipper">Shipper</SelectItem>
                        <SelectItem value="dispatcher">Dispatcher</SelectItem>
                        <SelectItem value="owner_operator">Owner Op</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant={foundingFilter ? "default" : "outline"}
                      onClick={() => setFoundingFilter(!foundingFilter)}
                      className={`flex-shrink-0 ${foundingFilter ? "bg-yellow-500 text-black hover:bg-yellow-400" : "border-yellow-500/30 text-yellow-400"}`}
                    >
                      <Crown className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Founding</span>
                    </Button>
                    <Button variant="outline" onClick={loadMembers} className="flex-shrink-0">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                {membersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-muted-foreground text-center py-12">
                    No members found matching your filters
                  </p>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="w-full text-sm min-w-[600px]">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-3 text-gray-400 font-medium text-xs sm:text-sm">Member</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium text-xs sm:text-sm">Role</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium text-xs sm:text-sm">MC/DOT</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium text-xs sm:text-sm">Founding</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium text-xs sm:text-sm">Rate</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium text-xs sm:text-sm">Status</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium text-xs sm:text-sm">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((member) => (
                          <tr key={member.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-3">
                              <div className="min-w-[140px]">
                                <p className="font-medium text-white text-sm truncate max-w-[160px]">{member.full_name || 'N/A'}</p>
                                <p className="text-xs text-gray-500 truncate max-w-[160px]">{member.email}</p>
                                {member.company_name && (
                                  <p className="text-xs text-gray-400 flex items-center gap-1 mt-1 truncate max-w-[160px]">
                                    <Building className="w-3 h-3 flex-shrink-0" /> {member.company_name}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <RoleBadge role={member.role} />
                            </td>
                            <td className="py-3 px-2">
                              <div className="text-xs whitespace-nowrap">
                                {member.mc_number && <p>MC# {member.mc_number}</p>}
                                {member.dot_number && <p>DOT# {member.dot_number}</p>}
                                {!member.mc_number && !member.dot_number && <span className="text-gray-500">-</span>}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              {member.founding_member_tier ? (
                                <div className="flex items-center gap-1 whitespace-nowrap">
                                  <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                                  <span className="text-yellow-400 text-xs font-medium capitalize">
                                    {member.founding_member_tier.replace('_', ' ')}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              {member.locked_price ? (
                                <div className="flex items-center gap-1 text-[#4ADE80] whitespace-nowrap">
                                  <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                                  <span className="font-medium text-xs sm:text-sm">${member.locked_price}</span>
                                </div>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <VerificationBadge status={member.verification_status} size="sm" />
                            </td>
                            <td className="py-3 px-2 text-gray-400 text-xs whitespace-nowrap">
                              {member.created_at ? new Date(member.created_at).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-4 text-xs sm:text-sm text-gray-500 text-center px-4 pb-4 sm:pb-0">
                      Showing {members.length} member(s)
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="border-white/5 bg-card/50">
              <CardContent className="pt-6">
                {users.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No pending user verifications
                  </p>
                ) : (
                  <div className="space-y-4">
                    {users.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-4 rounded-sm bg-secondary/30 border border-white/5"
                        data-testid={`admin-user-${user.user_id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <p className="font-semibold text-lg">{user.full_name}</p>
                            <RoleBadge role={user.role} />
                            <VerificationBadge status={user.verification_status} size="sm" />
                          </div>
                          <div className="grid md:grid-cols-3 gap-2 text-sm text-muted-foreground">
                            <span>Email: {user.email}</span>
                            {user.company_name && <span>Company: {user.company_name}</span>}
                            {user.mc_number && <span>MC#: {user.mc_number}</span>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleVerifyUser(user.user_id, 'verified')}
                            className="glow-gold"
                            data-testid={`verify-user-${user.user_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Verify
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleVerifyUser(user.user_id, 'rejected')}
                            data-testid={`reject-user-${user.user_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleVerifyUser(user.user_id, 'suspended')}
                          >
                            Suspend
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card className="border-white/5 bg-card/50">
              <CardContent className="pt-6">
                {documents.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No pending documents
                  </p>
                ) : (
                  <div className="space-y-4">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-4 rounded-sm bg-secondary/30 border border-white/5"
                        data-testid={`admin-doc-${doc.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-sm bg-secondary flex items-center justify-center">
                            <FileText className="w-6 h-6" />
                          </div>
                          <div>
                            <p className="font-medium capitalize">
                              {doc.document_type.replace('_', ' ')}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              User ID: {doc.user_id.slice(0, 8)}... • Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                            </p>
                            {doc.notes && (
                              <p className="text-sm text-muted-foreground mt-1">
                                Notes: {doc.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleReviewDocument(doc.id, 'approved')}
                            className="glow-gold"
                            data-testid={`approve-doc-${doc.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              const reason = prompt('Rejection reason:');
                              if (reason) handleReviewDocument(doc.id, 'rejected', reason);
                            }}
                            data-testid={`reject-doc-${doc.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fraud Shield Tab */}
          <TabsContent value="fraud-shield">
            <FraudShieldAdmin />
          </TabsContent>
        </Tabs>
      </div>

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={cleanupDialogOpen} onOpenChange={setCleanupDialogOpen}>
        <DialogContent className="bg-[#12151a] border-[#1a1d24]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Clear Test Data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4">
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Trash2 className="w-4 h-4 text-red-400" />
              All loads and load requests
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Trash2 className="w-4 h-4 text-red-400" />
              All test users (emails containing "test")
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Trash2 className="w-4 h-4 text-red-400" />
              All inspections, photos, and BOLs
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Trash2 className="w-4 h-4 text-red-400" />
              All ratings and notifications
            </div>
          </div>
          
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-sm text-red-400">
              ⚠️ This action cannot be undone. Your admin account and real user accounts will NOT be deleted.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanupDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCleanupTestData}
              disabled={cleaning}
              data-testid="confirm-cleanup-btn"
            >
              {cleaning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cleaning...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Test Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
